import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CloudUpload, Copy, ExternalLink, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/AuthContext";
import { Button, Card, ErrorNote, Field, Loading, Modal, Note, inputClass } from "@/components/portal/Ui";

const CONSOLE = "https://console.cloud.google.com";
const FAILURE_CAUSES = {
  redirect_mismatch:
    "Google rejected the redirect address. Open the OAuth client and check the redirect URI below is listed under Authorised redirect URIs, character for character.",
  api_disabled:
    "The Google Drive API is not switched on for that project. Enable it in the API Library, then connect again.",
  scope_missing:
    "Drive access was not granted on the permission screen. Connect again and leave the Google Drive permission ticked.",
  expired: "That sign-in took too long and expired. Start the connection again.",
  other: "Google did not finish the connection. Work down the setup steps and try again.",
};

function CopyValue({ value, testId }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <code className="break-all rounded-lg bg-[#F4F1EB] px-2.5 py-1.5 font-mono text-xs" data-testid={testId}>
        {value || "—"}
      </code>
      <button
        type="button"
        disabled={!value}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast.success("Copied");
          } catch {
            toast.error("Copy failed — select the text and copy it manually");
          }
        }}
        data-testid={`${testId}-copy`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F2811D] hover:underline disabled:opacity-40"
      >
        <Copy className="h-3.5 w-3.5" />
        Copy
      </button>
    </div>
  );
}

function Step({ number, title, done, why, children }) {
  return (
    <li
      className={`rounded-xl border p-4 ${done ? "border-[#EFEAE1] bg-[#FBFAF7]" : "border-[#E6E1D8] bg-white"}`}
      data-testid={`drive-step-${number}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            done ? "bg-[#EAF6EE] text-[#1B6B3A]" : "bg-[#111111] text-white"
          }`}
          data-testid={`drive-step-${number}-state`}
        >
          {done ? <Check className="h-3.5 w-3.5" /> : number}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className={`text-sm font-extrabold ${done ? "text-[#8a8378]" : ""}`}>{title}</h4>
          {why && <p className="mt-0.5 text-xs body-text">{why}</p>}
          <div className={`mt-2 text-sm leading-relaxed ${done ? "body-text" : ""}`}>{children}</div>
        </div>
      </div>
    </li>
  );
}

function Learn({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 font-semibold text-[#F2811D] hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/** What is left to do in Google Cloud Console, in the order it has to be done. */
function SetupGuide({ status }) {
  const configured = Boolean(status.configured);
  const connected = Boolean(status.connected);
  const foldered = Boolean(status.folder_id);
  return (
    <ol className="mt-4 space-y-3" data-testid="drive-setup-guide">
      <Step number={1} title="Open the Google Cloud project that holds the OAuth client" done={configured}
            why="So the rest of the steps are made on the right project.">
        <Learn href={`${CONSOLE}/apis/credentials`}>Open Credentials in Google Cloud Console</Learn>
        <p className="mt-2 body-text">
          The client to look for — if the project list is long, this is the ID shown beside it:
        </p>
        <CopyValue value={status.client_id} testId="drive-step-client-id" />
        <p className="mt-2 text-xs body-text">
          Its type must read <strong>Web application</strong>. If it says Desktop or another type,
          create a new OAuth client ID of type Web application — only a Web application client can
          use a redirect address.
        </p>
      </Step>

      <Step number={2} title="Switch on the Google Drive API" done={connected}
            why="Without it, connecting gets all the way to the end and then fails.">
        <Learn href={`${CONSOLE}/apis/library/drive.googleapis.com`}>Open the Google Drive API page</Learn>
        <p className="mt-2 body-text">
          If the button reads <strong>Enable</strong>, click it. If it already reads{" "}
          <strong>Manage</strong> with a green "API enabled" line, this step is done.
        </p>
      </Step>

      <Step number={3} title="Check the consent screen" done={connected}
            why="This is the screen you will see when you sign in — it should name AnyTribe.">
        <Learn href={`${CONSOLE}/apis/credentials/consent`}>Open the OAuth consent screen</Learn>
        <p className="mt-2 body-text">
          User type will be <strong>External</strong> (the only option for a plain Gmail account).
          Make sure the AnyTribe Gmail address is listed under <strong>Test users</strong>, and that
          the app name is one you are happy to see on the sign-in screen.
        </p>
      </Step>

      <Step number={4} title="Publish the app" done={connected}
            why="Left in Testing, Google expires the connection after seven days and someone has to reconnect every week.">
        <p className="body-text">
          On the same consent screen, if <strong>Publishing status</strong> reads{" "}
          <strong>Testing</strong>, click <strong>Publish app</strong> and confirm. It should then
          read <strong>In production</strong>.
        </p>
        <p className="mt-2 text-xs body-text">
          Publishing does not submit anything for review. The only consequence is a one-time
          "Google hasn't verified this app" warning the first time you connect — step 7 says how to
          click past it. Verification itself is a long review meant for apps serving the public; only
          the AnyTribe account ever signs in here.
        </p>
      </Step>

      <Step number={5} title="Add the redirect address to that client" done={connected}
            why="Google refuses the connection if this is even one character out.">
        <p className="body-text">
          Open the OAuth client from step 1 and paste this under{" "}
          <strong>Authorised redirect URIs</strong> — not Authorised JavaScript origins — then save.
        </p>
        <CopyValue value={status.redirect_uri} testId="drive-step-redirect" />
        <p className="mt-2 text-xs body-text">
          This is the address of the app you are looking at now. Add the live site's address too so
          filing keeps working after publishing:
        </p>
        <CopyValue value="https://anytribemarketing.com/api/drive/callback" testId="drive-step-redirect-live" />
      </Step>

      <Step number={6} title="Hand over the Client ID and Secret" done={configured}
            why="The two values the app signs in with. They are entered in the platform's secure key card, never typed into a page or a chat message.">
        {configured ? (
          <p className="body-text">
            Both values are in place — the app is configured.
          </p>
        ) : (
          <p className="body-text">
            On the OAuth client screen, copy the <strong>Client ID</strong> and{" "}
            <strong>Client secret</strong> (use <strong>Reset secret</strong> if the secret was never
            saved anywhere), then ask in chat for the secure key card and enter them there. Anything
            pasted into a chat message should be rotated afterwards.
          </p>
        )}
      </Step>

      <Step number={7} title="Connect the account and pick the folder" done={foldered}
            why="The last step, and the only one that happens in this app.">
        <p className="body-text">
          Click <strong>Connect Google Drive</strong> above and sign in as the AnyTribe Gmail
          account. On the "Google hasn't verified this app" screen, click{" "}
          <strong>Advanced</strong> and then <strong>Go to … (unsafe)</strong> — that warning is
          expected and appears once. Leave the Google Drive permission ticked, then choose the folder
          invoices should land in.
        </p>
      </Step>
    </ol>
  );
}

/** One agency-wide Google Drive connection and the folder approved invoices are filed into. */
export function DriveSettings({ status, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [guide, setGuide] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    const flag = params.get("drive");
    if (!flag) return;
    if (flag === "connected") toast.success("Google Drive connected — now pick a folder");
    if (flag === "failed") {
      const reason = params.get("reason") || "other";
      setFailure(FAILURE_CAUSES[reason] || FAILURE_CAUSES.other);
      setGuide(true);
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("drive");
        next.delete("reason");
        return next;
      },
      { replace: true }
    );
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  if (!status) return null;

  const done = status.configured && status.connected && status.folder_id;
  const statusText = !status.configured
    ? "Not switched on yet — the Google OAuth Client ID and Secret still have to be handed over. Work down the setup steps to finish it."
    : !status.connected
      ? "Configured and ready to connect. Sign in once as the AnyTribe Google account."
      : !status.folder_id
        ? `Connected by ${status.connected_by_name}. Pick the folder invoices should be filed into.`
        : `Filing into ${status.folder_name || status.folder_id}. Connected by ${status.connected_by_name} on ${formatDate(status.connected_at)}.`;

  return (
    <Card className="mb-5" testId="drive-settings-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-extrabold">
            <CloudUpload className="h-4 w-4 text-[#A85A05]" />
            Invoice filing to Google Drive
          </h2>
          <p className="mt-1.5 text-sm body-text" data-testid="drive-status-text">
            {statusText}
          </p>
          {status.folder_link && (
            <a
              href={status.folder_link}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="drive-folder-link"
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F2811D] hover:underline"
            >
              Open the folder in Drive <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {status.configured && (
              <Button variant="outline" busy={busy} onClick={connect} testId="drive-connect-button">
                {status.connected ? "Reconnect account" : "Connect Google Drive"}
              </Button>
            )}
            {status.connected && (
              <Button variant="outline" onClick={() => setPicker(true)} testId="drive-pick-folder-button">
                <FolderOpen className="h-4 w-4" />
                {status.folder_id ? "Change folder" : "Pick folder"}
              </Button>
            )}
            <Button
              variant={done ? "outline" : "primary"}
              onClick={() => setGuide((open) => !open)}
              testId="drive-setup-toggle"
            >
              {guide ? "Hide the steps" : done ? "Setup steps" : "Set up Drive filing"}
            </Button>
          </div>
        )}
      </div>

      {isAdmin && failure && (
        <div className="mt-3">
          <Note tone="red" testId="drive-failure-note">
            {failure}
          </Note>
        </div>
      )}

      {isAdmin && guide && <SetupGuide status={status} />}

      {isAdmin && done && <FilingBehaviour status={status} onChanged={onChanged} />}

      <FolderPicker
        open={picker}
        onClose={() => setPicker(false)}
        onSaved={async () => {
          setPicker(false);
          await onChanged?.();
        }}
      />
    </Card>
  );
}

/** How filing behaves once the connection is live: on approval, sub-folders, monthly sheet. */
function FilingBehaviour({ status, onChanged }) {
  const [saving, setSaving] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [summarising, setSummarising] = useState(false);

  const save = async (key, value, message) => {
    setSaving(key);
    try {
      await api.put("/drive/settings", { [key]: value });
      toast.success(message);
      await onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving("");
    }
  };

  const fileSummary = async () => {
    setSummarising(true);
    try {
      const { data } = await api.post("/drive/summary", { month });
      toast.success(`${data.invoices} invoices written to ${data.name}`);
      await onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSummarising(false);
    }
  };

  return (
    <div className="mt-4 space-y-3 border-t border-[#EFEAE1] pt-4" data-testid="drive-filing-behaviour">
      <Toggle
        checked={Boolean(status.auto_file)}
        busy={saving === "auto_file"}
        onChange={(value) =>
          save("auto_file", value, value ? "Invoices will be filed on approval" : "Filing on approval is off")
        }
        testId="drive-auto-file-toggle"
        title="File an invoice the moment it is approved"
        body="Applies to every campaign unless a campaign is set otherwise on its own page. Approval never fails because of Drive — if filing does not work, the invoice says why and Send to Drive still works."
      />
      <Toggle
        checked={Boolean(status.campaign_subfolders)}
        busy={saving === "campaign_subfolders"}
        onChange={(value) =>
          save("campaign_subfolders", value,
            value ? "Each campaign gets its own folder" : "Invoices will be filed into the one folder")
        }
        testId="drive-subfolders-toggle"
        title="Give each campaign its own folder"
        body="A folder named after the campaign is created inside the destination folder the first time one of its invoices is filed. Invoices already filed stay where they are."
      />
      <div className="rounded-xl border border-[#EFEAE1] p-4">
        <h4 className="text-sm font-extrabold">Monthly invoice summary</h4>
        <p className="mt-0.5 text-xs body-text">
          One sheet of every invoice dated in the month — KOL, campaigns, amount, status and payment
          reference — written into the destination folder.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={`${inputClass} max-w-[190px]`}
            data-testid="drive-summary-month"
          />
          <Button variant="outline" busy={summarising} onClick={fileSummary} testId="drive-summary-send">
            Write the sheet to Drive
          </Button>
        </div>
        {status.last_summary && (
          <p className="mt-2 text-xs body-text" data-testid="drive-last-summary">
            Last written: {status.last_summary.month} ({status.last_summary.invoices} invoices) by{" "}
            {status.last_summary.sent_by_name} on {formatDate(status.last_summary.sent_at)} ·{" "}
            <a
              href={status.last_summary.web_link}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold text-[#F2811D] hover:underline"
            >
              Open the sheet
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, busy, title, body, testId }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#EFEAE1] p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={busy}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-[#F2811D]"
        data-testid={testId}
      />
      <span className="min-w-0">
        <span className="block text-sm font-extrabold">{title}</span>
        <span className="mt-0.5 block text-xs body-text">{body}</span>
      </span>
    </label>
  );
}

function FolderPicker({ open, onClose, onSaved }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");

  const load = useCallback(async () => {
    setRows(null);
    setError("");
    try {
      const { data } = await api.get("/drive/folders", {
        params: search ? { search } : {},
      });
      setRows(data);
    } catch (e) {
      setError(apiError(e));
      setRows([]);
    }
  }, [search]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const choose = async (folder) => {
    setSaving(folder.id);
    try {
      await api.put("/drive/folder", {
        folder_id: folder.id,
        folder_name: folder.name,
        folder_link: folder.webViewLink || null,
      });
      toast.success(`Invoices will be filed into ${folder.name}`);
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving("");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Choose the invoice folder" testId="drive-folder-modal">
      <div className="space-y-4">
        <Note tone="neutral">
          Invoices are copied into this one folder. The original document stays in AnyTribe as the
          record.
        </Note>
        <Field label="Search your Drive folders">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="e.g. Invoices 2026"
            className={inputClass}
            data-testid="drive-folder-search"
          />
        </Field>
        <ErrorNote message={error} onRetry={load} />
        {rows === null ? (
          <Loading label="Reading your Drive…" />
        ) : rows.length === 0 ? (
          <p className="text-sm body-text">No folders matched. Try a different name.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((folder) => (
              <li
                key={folder.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EFEAE1] p-3"
                data-testid={`drive-folder-${folder.id}`}
              >
                <span className="text-sm font-semibold">{folder.name}</span>
                <Button
                  variant="outline"
                  busy={saving === folder.id}
                  onClick={() => choose(folder)}
                  testId={`drive-folder-choose-${folder.id}`}
                >
                  Use this folder
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
